import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { Checkbox } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import VaccineReservationChart from "./VaccineReservationChart";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/THSarabunNew.ttf");
    if (!fontResponse.ok) {
      throw new Error(`Failed to fetch Sarabun font: ${fontResponse.statusText}`);
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const statusOptions = [
  { value: "ใบจองใหม่", label: "ใบจองใหม่" },
  { value: "รอการอนุมัติ", label: "รอการอนุมัติ" },
  { value: "ยืนยันการจอง", label: "ยืนยันการจอง" },
  { value: "พร้อมรับวัคซีน", label: "พร้อมรับวัคซีน" },
  { value: "รับวัคซีนแล้ว", label: "รับวัคซีนแล้ว" },
  { value: "ยกเลิกการจอง", label: "ยกเลิกการจอง" },
  { value: "ปฏิเสธการจอง", label: "ปฏิเสธการจอง" },
  { value: "เกินกำหนดรับ", label: "เกินกำหนดรับ" },
];

const VaccineReservationReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDeliveryPeriod: "",
    EndDeliveryPeriod: "",
    PickupPoint: "",
    VaccineID: "",
    ReservationStatus: "",
  });
  const [viewMode, setViewMode] = useState("byVaccine"); // 'byVaccine' or 'byReservation'
  const [deliveryPeriodOptions, setDeliveryPeriodOptions] = useState([]);
  const [pickupPointOptions, setPickupPointOptions] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [flatData, setFlatData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(false);

  // ดึงข้อมูล Delivery Periods
  const fetchDeliveryPeriods = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationPeriodList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setDeliveryPeriodOptions(response.DeliveryPeriods || []);
      } else {
        console.error("Failed to fetch delivery periods:", response.error);
      }
    } catch (error) {
      console.error("Error fetching delivery periods:", error);
    }
  };

  // ดึงข้อมูล Pickup Points
  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPointOptions(response.VaccineWarehousesList || []);
      } else {
        console.error("Failed to fetch pickup points:", response.error);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // เรียก API getVaccineReservationSummary
  const fetchReservationReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationReport",
        data: {
          StartDeliveryPeriod: filterData.StartDeliveryPeriod || "",
          EndDeliveryPeriod: filterData.EndDeliveryPeriod || "",
          PickupPoint: filterData.PickupPoint || "",
          VaccineID: filterData.VaccineID || "",
          ReservationStatus: filterData.ReservationStatus || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.summary || [];
        setFlatData(data);
        // Group data based on viewMode
        if (viewMode === "byVaccine") {
          const grouped = {};
          data.forEach((item) => {
            const key = item.VaccineNameTH;
            // ใช้ PackageSize จาก API response หรือหาจาก vaccineOptions
            const packageSize = item.PackageSize || (vaccineOptions.find((v) => v.VaccineID === item.VaccineID)?.PackageSize) || 1;
            const requestQuantityDoses = (item.RequestQuantity || 0) * packageSize;
            
            if (!grouped[key]) {
              grouped[key] = {
                VaccineNameTH: item.VaccineNameTH,
                VaccineNameEN: item.VaccineNameEN,
                TotalRequestQuantity: 0,
                TotalRequestQuantityDoses: 0,
                TotalPrice: 0,
                details: [],
              };
            }
            grouped[key].TotalRequestQuantity += item.RequestQuantity || 0;
            grouped[key].TotalRequestQuantityDoses += requestQuantityDoses;
            grouped[key].TotalPrice += Number(item.TotalPrice) || 0;
            grouped[key].details.push({
              PickupPointName: item.PickupPointName,
              ReservationID: item.ReservationID,
              VaccineNameTH: item.VaccineNameTH,
              RequestQuantity: item.RequestQuantity,
              RequestQuantityDoses: requestQuantityDoses,
              UnitPrice: item.UnitPrice,
              TotalPrice: item.TotalPrice,
              ReservationStatus: item.ReservationStatus,
              DeliveryPeriod: item.DeliveryPeriod,
            });
          });
          setGroupedData(Object.values(grouped));
        } else {
          const grouped = {};
          data.forEach((item) => {
            const key = item.ReservationID;
            // ใช้ PackageSize จาก API response หรือหาจาก vaccineOptions
            const packageSize = item.PackageSize || (vaccineOptions.find((v) => v.VaccineID === item.VaccineID)?.PackageSize) || 1;
            const requestQuantityDoses = (item.RequestQuantity || 0) * packageSize;
            
            if (!grouped[key]) {
              grouped[key] = {
                ReservationID: item.ReservationID,
                PickupPointName: item.PickupPointName,
                DeliveryPeriod: item.DeliveryPeriod,
                FarmerName: item.FarmerName,
                FarmerTel: item.FarmerTel,
                TotalRequestQuantity: 0,
                TotalRequestQuantityDoses: 0,
                ReservationStatus: item.ReservationStatus,
                details: [],
              };
            }
            grouped[key].TotalRequestQuantity += item.RequestQuantity || 0;
            grouped[key].TotalRequestQuantityDoses += requestQuantityDoses;
            grouped[key].details.push({
              ReservationID: key,
              VaccineNameTH: item.VaccineNameTH,
              RequestQuantity: item.RequestQuantity,
              RequestQuantityDoses: requestQuantityDoses,
              UnitPrice: item.UnitPrice,
              TotalPrice: item.TotalPrice,
            });
          });
          setGroupedData(Object.values(grouped));
        }
      } else {
        setFlatData([]);
        setGroupedData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching reservation report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchDeliveryPeriods();
      fetchPickupPoints();
      fetchVaccineOptions();
    }
  }, [sessionId]);

  useEffect(() => {
    if (flatData.length > 0) {
      fetchReservationReport(); // Re-group data when viewMode changes
    }
  }, [viewMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columnsByVaccine = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสั่งจอง (ขวด)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalRequestQuantity)?.toLocaleString(),
      sortable: true,
      width: "250px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสั่งจอง (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalRequestQuantityDoses)?.toLocaleString(),
      sortable: true,
      width: "250px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ราคา/ขวด</span>
        </HStack>
      ),
      selector: (row) => {
        const quantity = Number(row.TotalRequestQuantity) || 0;
        const totalPrice = Number(row.TotalPrice) || 0;
        const unitPrice = quantity > 0 ? (totalPrice / quantity).toFixed(2) : 0;
        return Number(unitPrice)?.toLocaleString();
      },
      sortable: true,
      width: "250px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ราคารวม</span>
        </HStack>
      ),
      selector: (row) => {
        const quantity = Number(row.TotalRequestQuantity) || 0;
        const totalPrice = Number(row.TotalPrice) || 0;
        const unitPrice = quantity > 0 ? (totalPrice / quantity).toFixed(2) : 0;
        return Number(totalPrice)?.toLocaleString();
      },
      sortable: true,
      width: "250px",
    },
  ];

  const columnsByReservation = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เลขที่ใบจอง</span>
        </HStack>
      ),
      selector: (row) => row.ReservationID,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>คลังวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.PickupPointName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รอบการจอง</span>
        </HStack>
      ),
      selector: (row) => row.DeliveryPeriod || "",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อเกษตรกร</span>
        </HStack>
      ),
      selector: (row) => row.FarmerName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เบอร์โทร</span>
        </HStack>
      ),
      selector: (row) => row.FarmerTel,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสั่งจอง (ขวด)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalRequestQuantity)?.toLocaleString(),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสั่งจอง (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalRequestQuantityDoses)?.toLocaleString(),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>สถานะ</span>
        </HStack>
      ),
      selector: (row) => row.ReservationStatus,
      sortable: true,
    },
  ];

  // Detail columns สำหรับมุมมองแยกตามชนิดวัคซีน
  const detailColumnsByVaccine = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID || "",
      sortable: true,
    },
    {
      name: "ชื่อวัคซีน",
      selector: (row) => row.VaccineNameTH || "",
      sortable: true,
    },
    {
      name: "จุดจำหน่าย/คลังวัคซีน",
      selector: (row) => row.PickupPointName || "",
      sortable: true,
    },
    {
      name: "จำนวนสั่งจอง (ขวด)",
      selector: (row) => Number(row.RequestQuantity)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "จำนวนสั่งจอง (โด๊ส)",
      selector: (row) => Number(row.RequestQuantityDoses)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "ราคา/ขวด",
      selector: (row) => Number(row.UnitPrice)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "ราคารวม",
      selector: (row) => Number(row.TotalPrice)?.toLocaleString(),
      sortable: true,
    },
  ];

  // Detail columns สำหรับมุมมองแยกตามเลขที่ใบจอง
  const detailColumnsByReservation = [
    {
      name: "วัคซีน",
      selector: (row) => row.VaccineNameTH || "",
      sortable: true,
    },
    {
      name: "จำนวนสั่งจอง (ขวด)",
      selector: (row) => Number(row.RequestQuantity)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "จำนวนสั่งจอง (โด๊ส)",
      selector: (row) => Number(row.RequestQuantityDoses)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "ราคา/ขวด",
      selector: (row) => Number(row.UnitPrice)?.toLocaleString(),
      sortable: true,
    },
    {
      name: "ราคารวม",
      selector: (row) => Number(row.TotalPrice)?.toLocaleString(),
      sortable: true,
    },
  ];

  const ExpandedComponent = ({ data }) => {
    const currentViewMode = viewMode;
    return (
      <Box p={4} bg="gray.50">
        <DataTable
          columns={currentViewMode === "byVaccine" ? detailColumnsByVaccine : detailColumnsByReservation}
          data={data.details}
          customStyles={tableCustomStyles}
          dense
          noHeader
        />
      </Box>
    );
  };

  // ฟังก์ชันสำหรับดึงรายละเอียดทั้งหมด
  const fetchAllDetails = () => {
    const allDetails = [];
    groupedData.forEach((item) => {
      if (item.details && item.details.length > 0) {
        allDetails.push(...item.details);
      }
    });
    return allDetails;
  };

  const exportToExcel = async (includeDetails = false) => {
    const wb = XLSX.utils.book_new();
    
    // ข้อมูลเงื่อนไข
    const pickupPointName = filterData.PickupPoint
      ? pickupPointOptions.find((p) => p.WarehouseID === filterData.PickupPoint)?.WarehouseName || ""
      : "";
    const startPeriod = filterData.StartDeliveryPeriod
      ? deliveryPeriodOptions.find((p) => p.DeliveryPeriod === filterData.StartDeliveryPeriod)?.DeliveryPeriod || ""
      : "";
    const endPeriod = filterData.EndDeliveryPeriod
      ? deliveryPeriodOptions.find((p) => p.DeliveryPeriod === filterData.EndDeliveryPeriod)?.DeliveryPeriod || ""
      : "";
    const reportDate = new Date().toLocaleString("th-TH");

    // สร้างข้อมูลสรุปตาม viewMode
    let summaryData = [];
    if (viewMode === "byVaccine") {
      summaryData = groupedData.map((item) => {
        const quantity = Number(item.TotalRequestQuantity) || 0;
        const totalPrice = Number(item.TotalPrice) || 0;
        const unitPrice = quantity > 0 ? (totalPrice / quantity).toFixed(2) : 0;
        return {
          "ชื่อวัคซีน (TH)": item.VaccineNameTH,
          "จำนวนสั่งจอง (ขวด)": item.TotalRequestQuantity,
          "จำนวนสั่งจอง (โด๊ส)": item.TotalRequestQuantityDoses,
          "ราคา/ขวด": unitPrice,
          "ราคารวม": item.TotalPrice,
        };
      });
    } else {
      summaryData = groupedData.map((item) => ({
        "เลขที่ใบจอง": item.ReservationID,
        "คลังวัคซีน": item.PickupPointName,
        "รอบการจอง": item.DeliveryPeriod || "",
        "ชื่อเกษตรกร": item.FarmerName,
        "เบอร์โทร": item.FarmerTel,
        "จำนวนสั่งจอง (ขวด)": item.TotalRequestQuantity,
        "จำนวนสั่งจอง (โด๊ส)": item.TotalRequestQuantityDoses,
        "สถานะ": item.ReservationStatus,
      }));
    }

    // สร้าง header
    const headerData = [
      { A: "รายงานการสั่งจองวัคซีน" + (pickupPointName ? ` : จุดจำหน่าย / คลัง ${pickupPointName}` : "") },
      { A: (startPeriod || endPeriod) ? `รายงานการสั่งจองวัคซีน รอบการจอง : ${startPeriod}${endPeriod ? ` ถึง ${endPeriod}` : ""}` : "" },
      { A: `วันที่รายงาน : ${reportDate}` },
      {},
    ];

    const ws = XLSX.utils.json_to_sheet(headerData, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, summaryData, { origin: 5 });

    // จัด alignment ตัวเลข
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let row = 5; row <= range.e.r; row++) {
      if (viewMode === "byVaccine") {
        // คอลัมน์ B (1): จำนวนสั่งจอง (ขวด)
        // คอลัมน์ C (2): จำนวนสั่งจอง (โด๊ส)
        // คอลัมน์ D (3): ราคา/ขวด
        // คอลัมน์ E (4): ราคารวม
        for (let col = 1; col <= 4; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          ws[cellAddress].z = "#,##0.00";
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s.alignment = { horizontal: "right" };
        }
      } else {
        // คอลัมน์ F (5): จำนวนสั่งจอง (ขวด)
        // คอลัมน์ G (6): จำนวนสั่งจอง (โด๊ส)
        for (let col = 5; col <= 6; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;
          ws[cellAddress].z = "#,##0.00";
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s.alignment = { horizontal: "right" };
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, viewMode === "byVaccine" ? "สรุปตามวัคซีน" : "สรุปตามใบจอง");

    // ถ้าต้องการรายละเอียด
    if (includeDetails) {
      const allDetails = fetchAllDetails();
      let detailData = [];
      if (viewMode === "byVaccine") {
        detailData = allDetails.map((item) => ({
          "เลขที่ใบจอง": item.ReservationID,
          "ชื่อวัคซีน": item.VaccineNameTH || "",
          "จุดจำหน่าย/คลังวัคซีน": item.PickupPointName,
          "จำนวนสั่งจอง (ขวด)": item.RequestQuantity,
          "จำนวนสั่งจอง (โด๊ส)": item.RequestQuantityDoses,
          "ราคา/ขวด": item.UnitPrice,
          "ราคารวม": item.TotalPrice,
        }));
      } else {
        detailData = allDetails.map((item) => ({
          "เลขที่ใบจอง": item.ReservationID || "",
          "วัคซีน": item.VaccineNameTH || "",
          "จำนวนสั่งจอง (ขวด)": item.RequestQuantity,
          "จำนวนสั่งจอง (โด๊ส)": item.RequestQuantityDoses,
          "ราคา/ขวด": item.UnitPrice,
          "ราคารวม": item.TotalPrice,
        }));
      }

      const wsDetails = XLSX.utils.json_to_sheet(detailData);
      // จัด alignment ตัวเลข
      const detailRange = XLSX.utils.decode_range(wsDetails["!ref"] || "A1");
      for (let row = 1; row <= detailRange.e.r; row++) {
        if (viewMode === "byVaccine") {
          // คอลัมน์ D (3): จำนวนสั่งจอง (ขวด)
          // คอลัมน์ E (4): จำนวนสั่งจอง (โด๊ส)
          // คอลัมน์ F (5): ราคา/ขวด
          // คอลัมน์ G (6): ราคารวม
          for (let col = 3; col <= 6; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!wsDetails[cellAddress]) continue;
            wsDetails[cellAddress].z = "#,##0.00";
            if (!wsDetails[cellAddress].s) wsDetails[cellAddress].s = {};
            wsDetails[cellAddress].s.alignment = { horizontal: "right" };
          }
        } else {
          // คอลัมน์ C (2): จำนวนสั่งจอง (ขวด)
          // คอลัมน์ D (3): จำนวนสั่งจอง (โด๊ส)
          // คอลัมน์ E (4): ราคา/ขวด
          // คอลัมน์ F (5): ราคารวม
          for (let col = 2; col <= 5; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!wsDetails[cellAddress]) continue;
            wsDetails[cellAddress].z = "#,##0.00";
            if (!wsDetails[cellAddress].s) wsDetails[cellAddress].s = {};
            wsDetails[cellAddress].s.alignment = { horizontal: "right" };
          }
        }
      }
      XLSX.utils.book_append_sheet(wb, wsDetails, "รายละเอียดการจอง");
    }

    XLSX.writeFile(wb, "VaccineReservationReport.xlsx");
  };

  const exportToPDF = async (includeDetails = false) => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("THSarabunNew.ttf", fontBase64);
      doc.addFont("THSarabunNew.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    // ข้อมูลเงื่อนไข
    const pickupPointName = filterData.PickupPoint
      ? pickupPointOptions.find((p) => p.WarehouseID === filterData.PickupPoint)?.WarehouseName || ""
      : "";
    const startPeriod = filterData.StartDeliveryPeriod
      ? deliveryPeriodOptions.find((p) => p.DeliveryPeriod === filterData.StartDeliveryPeriod)?.DeliveryPeriod || ""
      : "";
    const endPeriod = filterData.EndDeliveryPeriod
      ? deliveryPeriodOptions.find((p) => p.DeliveryPeriod === filterData.EndDeliveryPeriod)?.DeliveryPeriod || ""
      : "";
    const reportDate = new Date().toLocaleString("th-TH");

    // ตั้งค่าหัวเรื่องและวันที่
    doc.setFontSize(16);
    doc.text("รายงานการสั่งจองวัคซีน" + (pickupPointName ? ` : จุดจำหน่าย / คลัง ${pickupPointName}` : ""), 14, 10);
    doc.setFontSize(10);
    let currentY = 18;
    if (startPeriod || endPeriod) {
      doc.text(`รายงานการสั่งจองวัคซีน รอบการจอง : ${startPeriod}${endPeriod ? ` ถึง ${endPeriod}` : ""}`, 14, currentY);
      currentY += 6;
    }
    doc.text(`วันที่รายงาน : ${reportDate}`, 14, currentY);
    currentY += 6;

    // สร้างตาราง
    autoTable(doc, {
      startY: currentY + 5,
      head:
        viewMode === "byVaccine"
          ? [
              [
                "ชื่อวัคซีน (TH)",
                "จำนวนสั่งจอง (ขวด)",
                "จำนวนสั่งจอง (โด๊ส)",
                "ราคา/ขวด",
                "ราคารวม",
              ],
            ]
          : [
              [
                "เลขที่ใบจอง",
                "คลังวัคซีน",
                "รอบการจอง",
                "ชื่อเกษตรกร",
                "เบอร์โทร",
                "จำนวนสั่งจอง (ขวด)",
                "จำนวนสั่งจอง (โด๊ส)",
                "สถานะ",
              ],
            ],
      body:
        viewMode === "byVaccine"
          ? groupedData.map((item) => {
              const quantity = Number(item.TotalRequestQuantity) || 0;
              const totalPrice = Number(item.TotalPrice) || 0;
              const unitPrice = quantity > 0 ? (totalPrice / quantity).toFixed(2) : 0;
              return [
                item.VaccineNameTH,
                Number(item.TotalRequestQuantity)?.toLocaleString(),
                Number(item.TotalRequestQuantityDoses)?.toLocaleString(),
                Number(unitPrice)?.toLocaleString(),
                Number(item.TotalPrice)?.toLocaleString(),
              ];
            })
          : groupedData.map((item) => [
              item.ReservationID,
              item.PickupPointName,
              item.DeliveryPeriod || "",
              item.FarmerName,
              item.FarmerTel,
              Number(item.TotalRequestQuantity)?.toLocaleString(),
              Number(item.TotalRequestQuantityDoses)?.toLocaleString(),
              item.ReservationStatus,
            ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 11,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },      
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fontSize: 11,
        halign: "center",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: viewMode === "byVaccine"
        ? {
            0: { cellWidth: 150, halign: "left" },
            1: { cellWidth: 30, halign: "right" },
            2: { cellWidth: 30, halign: "right" },
            3: { cellWidth: 30, halign: "right" },
            4: { cellWidth: 30, halign: "right" },
          }
        : {
            0: { cellWidth: 30, halign: "left" },
            1: { cellWidth: 50, halign: "left" },
            2: { cellWidth: 40, halign: "left" },
            3: { cellWidth: 50, halign: "left" },
            4: { cellWidth: 25, halign: "left" },
            5: { cellWidth: 25, halign: "right" },
            6: { cellWidth: 25, halign: "right" },
            7: { cellWidth: 30, halign: "left" },
          },
      didParseCell: (data) => {
        // จัด alignment ตัวเลข
        if (viewMode === "byVaccine") {
          if (data.column.index >= 1 && data.column.index <= 4) {
            data.cell.styles.halign = "right";
          }
        } else {
          if (data.column.index >= 5 && data.column.index <= 6) {
            data.cell.styles.halign = "right";
          }
        }
      },
    });

    // ถ้าต้องการรายละเอียด
    if (includeDetails) {
      const allDetails = fetchAllDetails();
      if (allDetails.length > 0) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text("รายละเอียดการจอง", 14, 10);
        
        autoTable(doc, {
          startY: 20,
          head: viewMode === "byVaccine"
            ? [
                [
                  "เลขที่ใบจอง",
                  "ชื่อวัคซีน",
                  "จุดจำหน่าย/คลังวัคซีน",
                  "จำนวนสั่งจอง (ขวด)",
                  "จำนวนสั่งจอง (โด๊ส)",
                  "ราคา/ขวด",
                  "ราคารวม",
                ],
              ]
            : [
                [
                  "เลขที่ใบจอง",
                  "วัคซีน",
                  "จำนวนสั่งจอง (ขวด)",
                  "จำนวนสั่งจอง (โด๊ส)",
                  "ราคา/ขวด",
                  "ราคารวม",
                ],
              ],
          body: allDetails.map((item) => {
            if (viewMode === "byVaccine") {
              return [
                item.ReservationID,
                item.VaccineNameTH || "",
                item.PickupPointName || "",
                Number(item.RequestQuantity)?.toLocaleString(),
                Number(item.RequestQuantityDoses)?.toLocaleString(),
                Number(item.UnitPrice)?.toLocaleString(),
                Number(item.TotalPrice)?.toLocaleString(),
              ];
            } else {
              return [
                item.ReservationID,
                item.VaccineNameTH || "",
                Number(item.RequestQuantity)?.toLocaleString(),
                Number(item.RequestQuantityDoses)?.toLocaleString(),
                Number(item.UnitPrice)?.toLocaleString(),
                Number(item.TotalPrice)?.toLocaleString(),
              ];
            }
          }),
          styles: {
            font: fontBase64 ? "Sarabun" : "helvetica",
            fontSize: 10,
            cellPadding: 1,
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255],
          },
          headStyles: {
            font: fontBase64 ? "Sarabun" : "helvetica",
            fontStyle: "normal",
            fontSize: 10,
            halign: "center",
            fillColor: [211, 211, 211],
            textColor: "black",
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
          },
          columnStyles: viewMode === "byVaccine"
            ? {
                0: { cellWidth: 30, halign: "left" },
                1: { cellWidth: 120, halign: "left" },
                2: { cellWidth: 50, halign: "left" },
                3: { cellWidth: 25, halign: "right" },
                4: { cellWidth: 25, halign: "right" },
                5: { cellWidth: 25, halign: "right" },
                6: { cellWidth: 25, halign: "right" },
              }
            : {
                0: { cellWidth: 30, halign: "left" },
                1: { cellWidth: 120, halign: "left" },
                2: { cellWidth: 25, halign: "right" },
                3: { cellWidth: 25, halign: "right" },
                4: { cellWidth: 25, halign: "right" },
                5: { cellWidth: 25, halign: "right" },
              },
        });
      }
    }

    doc.save("VaccineReservationReport.pdf");
  };

  const printTable = (includeDetails = false) => {
    // ข้อมูลเงื่อนไข
    const pickupPointName = filterData.PickupPoint
      ? pickupPointOptions.find((p) => p.WarehouseID === filterData.PickupPoint)?.WarehouseName || ""
      : "";
    const startPeriod = filterData.StartDeliveryPeriod
      ? deliveryPeriodOptions.find((p) => p.DeliveryPeriod === filterData.StartDeliveryPeriod)?.DeliveryPeriod || ""
      : "";
    const endPeriod = filterData.EndDeliveryPeriod
      ? deliveryPeriodOptions.find((p) => p.DeliveryPeriod === filterData.EndDeliveryPeriod)?.DeliveryPeriod || ""
      : "";
    const reportDate = new Date().toLocaleString("th-TH");

    let detailsHTML = "";
    if (includeDetails) {
      const allDetails = fetchAllDetails();
      if (allDetails.length > 0) {
        const detailHeaders = viewMode === "byVaccine"
          ? `
            <tr>
              <th>เลขที่ใบจอง</th>
              <th>ชื่อวัคซีน</th>
              <th>จุดจำหน่าย/คลังวัคซีน</th>
              <th class="text-right">จำนวนสั่งจอง (ขวด)</th>
              <th class="text-right">จำนวนสั่งจอง (โด๊ส)</th>
              <th class="text-right">ราคา/ขวด</th>
              <th class="text-right">ราคารวม</th>
            </tr>
          `
          : `
            <tr>
              <th>เลขที่ใบจอง</th>
              <th>วัคซีน</th>
              <th class="text-right">จำนวนสั่งจอง (ขวด)</th>
              <th class="text-right">จำนวนสั่งจอง (โด๊ส)</th>
              <th class="text-right">ราคา/ขวด</th>
              <th class="text-right">ราคารวม</th>
            </tr>
          `;
        
        const detailRows = allDetails.map((item) => {
          if (viewMode === "byVaccine") {
            return `
              <tr>
                <td>${item.ReservationID || ""}</td>
                <td>${item.VaccineNameTH || ""}</td>
                <td>${item.PickupPointName || ""}</td>
                <td class="text-right">${Number(item.RequestQuantity)?.toLocaleString()}</td>
                <td class="text-right">${Number(item.RequestQuantityDoses)?.toLocaleString()}</td>
                <td class="text-right">${Number(item.UnitPrice)?.toLocaleString()}</td>
                <td class="text-right">${Number(item.TotalPrice)?.toLocaleString()}</td>
              </tr>
            `;
          } else {
            return `
              <tr>
                <td>${item.ReservationID || ""}</td>
                <td>${item.VaccineNameTH || ""}</td>
                <td class="text-right">${Number(item.RequestQuantity)?.toLocaleString()}</td>
                <td class="text-right">${Number(item.RequestQuantityDoses)?.toLocaleString()}</td>
                <td class="text-right">${Number(item.UnitPrice)?.toLocaleString()}</td>
                <td class="text-right">${Number(item.TotalPrice)?.toLocaleString()}</td>
              </tr>
            `;
          }
        }).join("");

        detailsHTML = `
          <h2 style="margin-top: 30px;">รายละเอียดการจอง</h2>
          <table>
            <thead>
              ${detailHeaders}
            </thead>
            <tbody>
              ${detailRows}
            </tbody>
          </table>
        `;
      }
    }

    const printContent = `
      <html>
        <head>
          <title>รายงานการสั่งจองวัคซีน</title>
          <style>
            body { font-family: 'THSarabunNew', Arial, sans-serif; margin: 20px; }
            h2 { text-align: center; margin-bottom: 10px; }
            .info-section { margin-bottom: 15px; }
            .info-line { margin: 5px 0; font-size: 14px; }
            table { border-collapse: collapse; width: 100%; margin-top: 10px; }
            th, td { border: 1px solid black; padding: 8px; }
            th { background-color: #d3d3d3; text-align: center; font-weight: bold; }
            td { text-align: left; }
            .text-right { text-align: right !important; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h2>รายงานการสั่งจองวัคซีน${pickupPointName ? ` : จุดจำหน่าย / คลัง ${pickupPointName}` : ""}</h2>
          <div class="info-section">
            ${(startPeriod || endPeriod) ? `<div class="info-line">รายงานการสั่งจองวัคซีน รอบการจอง : ${startPeriod}${endPeriod ? ` ถึง ${endPeriod}` : ""}</div>` : ""}
            <div class="info-line">วันที่รายงาน : ${reportDate}</div>
          </div>
          <table>
            <thead>
              <tr>
                ${
                  viewMode === "byVaccine"
                    ? `
                      <th>ชื่อวัคซีน (TH)</th>
                      <th class="text-right">จำนวนสั่งจอง (ขวด)</th>
                      <th class="text-right">จำนวนสั่งจอง (โด๊ส)</th>
                      <th class="text-right">ราคา/ขวด</th>
                      <th class="text-right">ราคารวม</th>
                    `
                    : `
                      <th>เลขที่ใบจอง</th>
                      <th>คลังวัคซีน</th>
                      <th>รอบการจอง</th>
                      <th>ชื่อเกษตรกร</th>
                      <th>เบอร์โทร</th>
                      <th class="text-right">จำนวนสั่งจอง (ขวด)</th>
                      <th class="text-right">จำนวนสั่งจอง (โด๊ส)</th>
                      <th>สถานะ</th>
                    `
                }
              </tr>
            </thead>
            <tbody>
              ${
                viewMode === "byVaccine"
                  ? groupedData
                      .map(
                        (item) => {
                          const quantity = Number(item.TotalRequestQuantity) || 0;
                          const totalPrice = Number(item.TotalPrice) || 0;
                          const unitPrice = quantity > 0 ? (totalPrice / quantity).toFixed(2) : 0;
                          return `
                          <tr>
                            <td>${item.VaccineNameTH}</td>
                            <td class="text-right">${Number(item.TotalRequestQuantity)?.toLocaleString()}</td>
                            <td class="text-right">${Number(item.TotalRequestQuantityDoses)?.toLocaleString()}</td>
                            <td class="text-right">${Number(unitPrice)?.toLocaleString()}</td>
                            <td class="text-right">${Number(item.TotalPrice)?.toLocaleString()}</td>
                          </tr>
                        `;
                        }
                      )
                      .join("")
                  : groupedData
                      .map(
                        (item) => `
                          <tr>
                            <td>${item.ReservationID}</td>
                            <td>${item.PickupPointName}</td>
                            <td>${item.DeliveryPeriod || ""}</td>
                            <td>${item.FarmerName}</td>
                            <td>${item.FarmerTel}</td>
                            <td class="text-right">${Number(item.TotalRequestQuantity)?.toLocaleString()}</td>
                            <td class="text-right">${Number(item.TotalRequestQuantityDoses)?.toLocaleString()}</td>
                            <td>${item.ReservationStatus}</td>
                          </tr>
                        `
                      )
                      .join("")
              }
            </tbody>
          </table>
          ${detailsHTML}
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading size="lg" as="h2" mb={10} textAlign="center">
        รายงานการสั่งจองวัคซีน
      </Heading>

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
                  title="ค้นหาข้อมูลรายการสั่งจองวัคซีน"
                  bgColor="iBlue.600"
                  icon={faSearch}
          />           
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                รอบการจอง (เริ่ม)
              </FormLabel>
              <Select
                name="StartDeliveryPeriod"
                value={filterData.StartDeliveryPeriod}
                onChange={handleInputChange}
                placeholder="เลือกรอบการจอง (เริ่ม)"
                colorScheme="iBlue"
              >
                {deliveryPeriodOptions.map((period) => (
                  <option
                    key={period.DeliveryPeriod}
                    value={period.DeliveryPeriod}
                  >
                    {period.DeliveryPeriod}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                รอบการจอง (สิ้นสุด)
              </FormLabel>
              <Select
                name="EndDeliveryPeriod"
                value={filterData.EndDeliveryPeriod}
                onChange={handleInputChange}
                placeholder="เลือกรอบการจอง (สิ้นสุด)"
                colorScheme="iBlue"
              >
                {deliveryPeriodOptions.map((period) => (
                  <option
                    key={period.DeliveryPeriod}
                    value={period.DeliveryPeriod}
                  >
                    {period.DeliveryPeriod}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                คลังวัคซีน
              </FormLabel>
              <Select
                name="PickupPoint"
                value={filterData.PickupPoint}
                onChange={handleInputChange}
                placeholder="เลือกคลังวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {pickupPointOptions.map((point) => (
                  <option key={point.WarehouseID} value={point.WarehouseID}>
                    {point.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                สถานะการจอง
              </FormLabel>
              <Select
                name="ReservationStatus"
                value={filterData.ReservationStatus}
                onChange={handleInputChange}
                placeholder="เลือกสถานะ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDeliveryPeriod: "",
                    EndDeliveryPeriod: "",
                    PickupPoint: "",
                    VaccineID: "",
                    ReservationStatus: "",
                  })
                }
              />
              <Search onClick={fetchReservationReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* เลือกมุมมอง */}
      <Box mb={6}>
        <FormControl>
          <FormLabel fontSize="xl" color="gray">
            มุมมองรายงาน
          </FormLabel>
          <RadioGroup onChange={setViewMode} value={viewMode}>
            <HStack spacing={5}>
              <Radio value="byVaccine">แยกตามชนิดวัคซีน</Radio>
              <Radio value="byReservation">แยกตามเลขที่ใบจอง</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
      </Box>

      {/* ตาราง */}
      {groupedData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <HStack>
            <FontAwesomeIcon icon={faTable} />
            <Heading size="md">
              รายการการสั่งจองวัคซีน ({viewMode === "byVaccine" ? "แยกตามชนิดวัคซีน" : "แยกตามเลขที่ใบจอง"})
            </Heading>
          </HStack>
          <HStack spacing={5} m={5}>
            <Checkbox
              isChecked={includeDetails}
              onChange={(e) => setIncludeDetails(e.target.checked)}
              colorScheme="iBlue"
            >
              รวมรายละเอียดการจอง
            </Checkbox>
            <Spacer />
            <ExportExcel onClick={() => exportToExcel(includeDetails)} />
            <ExportPdf onClick={() => exportToPDF(includeDetails)} />
            <Print onClick={() => printTable(includeDetails)} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={viewMode === "byVaccine" ? columnsByVaccine : columnsByReservation}
              data={groupedData}
              pagination
              highlightOnHover
              striped
              responsive
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {flatData.length > 0 && (
        <VaccineReservationChart
          data={flatData}
          pickupPointOptions={pickupPointOptions}
          viewMode={viewMode}
        />
      )}
    </Box>
  );
};

export default VaccineReservationReport;